import { UploadedFile } from './uploaded-file.model';
import { EMPLOYEE_SHIFT_LABELS, EMPLOYEE_STATUS_LABELS, Employee, employeeDisplayName, isEmployedStatus } from './employee.model';

export enum EmployeeRecordType {
    Hire = 'hire',
    StatusChange = 'status-change',
    TitleChange = 'title-change',
    ManagerChange = 'manager-change',
    ShiftChange = 'shift-change',
    PerformanceReview = 'performance-review',
    Disciplinary = 'disciplinary',
    Training = 'training',
    Certification = 'certification',
    Separation = 'separation',
    Rehire = 'rehire',
    Note = 'note',
}

export const EMPLOYEE_RECORD_TYPE_LABELS: Record<EmployeeRecordType, string> = {
    [EmployeeRecordType.Hire]: 'Hire',
    [EmployeeRecordType.StatusChange]: 'Status Change',
    [EmployeeRecordType.TitleChange]: 'Title Change',
    [EmployeeRecordType.ManagerChange]: 'Manager Change',
    [EmployeeRecordType.ShiftChange]: 'Shift Change',
    [EmployeeRecordType.PerformanceReview]: 'Performance Review',
    [EmployeeRecordType.Disciplinary]: 'Disciplinary',
    [EmployeeRecordType.Training]: 'Training',
    [EmployeeRecordType.Certification]: 'Certification',
    [EmployeeRecordType.Separation]: 'Separation',
    [EmployeeRecordType.Rehire]: 'Rehire',
    [EmployeeRecordType.Note]: 'Note',
};

/** Record types HR staff create by hand (the rest are written automatically on employee edits). */
export const MANUAL_EMPLOYEE_RECORD_TYPES: readonly EmployeeRecordType[] = [
    EmployeeRecordType.Note,
    EmployeeRecordType.PerformanceReview,
    EmployeeRecordType.Disciplinary,
    EmployeeRecordType.Training,
    EmployeeRecordType.Certification,
];

export const EMPLOYEE_RECORD_TYPES_WITH_EXPIRATION: readonly EmployeeRecordType[] = [
    EmployeeRecordType.Training,
    EmployeeRecordType.Certification,
];

export interface EmployeeRecord {
    id: string;
    employeeId: string;
    type: EmployeeRecordType;
    title: string;
    /** Markdown. */
    description: string;
    effectiveDate: Date;
    /** Training / certification expiry; null when not applicable. */
    expirationDate: Date | null;
    /** For auto-logged changes: the value before the change (name, title, status label...). */
    previousValue: string;
    newValue: string;
    files: UploadedFile[];
    createdOn: Date;
    createdById: string;
    updatedOn: Date;
    updatedById: string;
}

export const DEFAULT_EMPLOYEE_RECORD: EmployeeRecord = {
    id: '',
    employeeId: '',
    type: EmployeeRecordType.Note,
    title: '',
    description: '',
    effectiveDate: new Date(),
    expirationDate: null,
    previousValue: '',
    newValue: '',
    files: [],
    createdOn: new Date(),
    createdById: '',
    updatedOn: new Date(),
    updatedById: '',
};

export function employeeRecordTypeHasExpiration(type: EmployeeRecordType): boolean {
    return EMPLOYEE_RECORD_TYPES_WITH_EXPIRATION.includes(type);
}

/** True when the record has an expiration on or before `now + days` (already expired counts). */
export function employeeRecordExpiresWithin(record: EmployeeRecord, days: number, now: Date = new Date()): boolean {
    if (!record.expirationDate) {
        return false;
    }
    const horizon = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
    return record.expirationDate.getTime() <= horizon.getTime();
}

export function employeeRecordIsExpired(record: EmployeeRecord, now: Date = new Date()): boolean {
    return !!record.expirationDate && record.expirationDate.getTime() < now.getTime();
}

export interface EmployeeChangeContext {
    actorId: string;
    effectiveDate: Date;
    createId: () => string;
    /** Resolves manager ids to names in manager-change records; ids are used when absent. */
    employeesById?: Record<string, Employee>;
    /** Optional free-text reason copied into status/separation records. */
    reason?: string;
}

/**
 * Compares an employee before and after an edit and returns the records that document the change:
 * hire (when `before` is null), title, manager, shift and status changes. The caller saves
 * the employee first, then these records.
 */
export function employeeChangeRecords(
    before: Employee | null,
    after: Employee,
    context: EmployeeChangeContext,
): EmployeeRecord[] {
    const records: EmployeeRecord[] = [];
    const base = (): Omit<EmployeeRecord, 'type' | 'title' | 'previousValue' | 'newValue'> => ({
        id: context.createId(),
        employeeId: after.id,
        description: context.reason?.trim() ?? '',
        effectiveDate: context.effectiveDate,
        expirationDate: null,
        files: [],
        createdOn: new Date(),
        createdById: context.actorId,
        updatedOn: new Date(),
        updatedById: context.actorId,
    });
    const managerName = (managerId: string | null): string => {
        if (!managerId) {
            return '';
        }
        const manager = context.employeesById?.[managerId];
        return manager ? employeeDisplayName(manager) : managerId;
    };

    if (!before) {
        records.push({
            ...base(),
            type: EmployeeRecordType.Hire,
            title: `hired as ${after.jobTitle || 'employee'}`,
            description: '',
            effectiveDate: after.hireDate ?? context.effectiveDate,
            previousValue: '',
            newValue: after.jobTitle,
        });
        return records;
    }

    if ((before.jobTitle || '') !== (after.jobTitle || '')) {
        records.push({
            ...base(),
            type: EmployeeRecordType.TitleChange,
            title: `title changed to ${after.jobTitle || '(none)'}`,
            description: '',
            previousValue: before.jobTitle,
            newValue: after.jobTitle,
        });
    }

    if ((before.managerEmployeeId ?? null) !== (after.managerEmployeeId ?? null)) {
        const newManager = managerName(after.managerEmployeeId);
        records.push({
            ...base(),
            type: EmployeeRecordType.ManagerChange,
            title: newManager ? `now reports to ${newManager}` : 'no longer reports to anyone',
            description: '',
            previousValue: managerName(before.managerEmployeeId),
            newValue: newManager,
        });
    }

    if ((before.shift ?? null) !== (after.shift ?? null)) {
        const newShift = after.shift ? EMPLOYEE_SHIFT_LABELS[after.shift] : '';
        records.push({
            ...base(),
            type: EmployeeRecordType.ShiftChange,
            title: newShift ? `moved to ${newShift}` : 'shift cleared',
            description: '',
            previousValue: before.shift ? EMPLOYEE_SHIFT_LABELS[before.shift] : '',
            newValue: newShift,
        });
    }

    if (before.status !== after.status) {
        const wasEmployed = isEmployedStatus(before.status);
        const nowEmployed = isEmployedStatus(after.status);
        const type = wasEmployed && !nowEmployed
            ? EmployeeRecordType.Separation
            : !wasEmployed && nowEmployed
                ? EmployeeRecordType.Rehire
                : EmployeeRecordType.StatusChange;
        const newLabel = EMPLOYEE_STATUS_LABELS[after.status];
        records.push({
            ...base(),
            type,
            title: type === EmployeeRecordType.Rehire ? 'rehired' : `status changed to ${newLabel}`,
            previousValue: EMPLOYEE_STATUS_LABELS[before.status],
            newValue: newLabel,
        });
    }

    return records;
}

export function compareEmployeeRecordsNewestFirst(a: EmployeeRecord, b: EmployeeRecord): number {
    const byEffective = (b.effectiveDate?.getTime() ?? 0) - (a.effectiveDate?.getTime() ?? 0);
    if (byEffective !== 0) {
        return byEffective;
    }
    return (b.createdOn?.getTime() ?? 0) - (a.createdOn?.getTime() ?? 0);
}
